import React, {useCallback} from 'react';
import {connect} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {GatewayGetSchema} from '../../../../api/DBSchema/Gateway';
import Separator from '../../../../components/Simple/Sepperator';
import EditorLayout from '../../../../components/Layouts/EditorLayout';
import SMCTextInput from '../../../../components/Simple/SMCTextInput';
import {useBoolean, useSMCSnackbar} from '../../../../utils/hooks';
import SetPlanModal from '../../../PopupPages/SetPlanModal';
import generateIcon from '../../../../assets/images/generate.png';
import SMCButton from '../../../../components/Simple/SMCButton';
import RowLayout from '../../../../components/Layouts/RowLayout';
import {generateRandomString} from '../../../../utils/utils';
import {SMCPaths} from '../../../../conf/Routes';
import SMCLink from '../../../../components/Simple/SMCLink';
import SMCIcon from '../../../../components/Simple/SMCIcon';
import {push} from 'connected-react-router';
import Icon from "@chkp/ci-front-common-icons";
import {SMCToggleSwitch} from "../../../../components/Simple/SMCToggleSwitch";

const pageSchema = GatewayGetSchema.pages.general;
const pageId = pageSchema.pageId;

/**
 *
 * @param {Object} param
 * @param {import('../../../../api/DBSchema/Gateway').Gateway} param.gatewayObject
 */
function GatewayGeneral({
    gatewayObject,
    setGatewayData,
    setPlanObject,
    modifyPermission,
    navigateTo
}) {
    const { t } = useTranslation();
    const { enqueueErrorSnackbar } = useSMCSnackbar();

    const [isChangePlanOpen, openChangePlan, closeChangePlan] = useBoolean(false);

    const pageData = gatewayObject.general;
    const setPageValue = (paramName, value) => {
        setGatewayData([pageId, paramName], value);
    }
    const activationKey = pageData.activationKey;
    // const activationKey = `172.23.40.92&${gatewayObject[GatewayGetSchema.id]}.${currentPortal}&${pageData[pageSchema.regKey]}`;

    const description      = pageData.description;
    const enabled          = pageData.enabled;
    const gatewayType      = pageData.gatewayType;
    const reportedFirmware = pageData.reportedFirmware;
    const managedBySmp     = pageData.managedBySmp;
    const planId           = gatewayObject.planId;
    const macAddress       = pageData.macAddress;
    const lastConnectedIp  = pageData.lastConnectedIpAddress;
    const registrationKey  = pageData.registrationKey;
    const staticIp         = pageData.staticIp;

    const isClusterGateway = gatewayObject[GatewayGetSchema.isClusterGateway];

    const copyActivationKey = useCallback(() => {
        navigator.clipboard.writeText(activationKey);
    }, [activationKey]);

    return (
        <EditorLayout>
            <SMCTextInput
                value={description}
                onChange={e => setPageValue(pageSchema.description, e.target.value)}
                label={t('SMP.DESCRIPTION')}
            />
            <SMCToggleSwitch
                checked={enabled}
                onChange={checked => setPageValue(pageSchema.enabled, checked)}
                label={t("SMP.ENABLED")}
                labelPosition="start"
            />
            {/* Gateway type */}
            <SMCTextInput
                readOnly
                value={gatewayType}
                label={t("SMP.GATEWAYS.PAGE.GENERAL.GW_TYPE")}
            />
            {/* Reported firmware */}
            <SMCTextInput
                readOnly
                value={reportedFirmware}
                label={t("SMP.GATEWAYS.PAGE.GENERAL.REPORTED_FIRMWARE")}
            />
            <Separator isMainSeparator/>
            {/* Managed by SMP */}
            <SMCToggleSwitch
                checked={managedBySmp}
                onChange={checked => setPageValue(pageSchema.managedBySmp, checked)}
                label={t('SMP.GATEWAYS.PAGE.GENERAL.MANAGED_BY_SMP')}
            />
            {/* Show this section only if managed in SMP */}
            {
                managedBySmp && (
                    <>
                        {/* Plan */}
                        <RowLayout>
                            <SMCTextInput
                                disabled
                                value={planId}
                                label={t("SMP.GATEWAYS.PAGE.GENERAL.PLAN")}
                                tooltip={t("SMC_GATEWAYWIZ_CREATE_NEW_GATEWAY_REMOTE_MANAGEMENT.TOOLTIP")}
                                inline
                            />
                            <SMCButton style={{ marginRight: '8px' }} onClick={openChangePlan}>Change Plan</SMCButton>
                            <SMCLink
                                disabled={!planId}
                                underline
                                onClick={() => navigateTo(SMCPaths.plans+'?id='+planId)}
                            >
                                <SMCIcon icon="rightArrow" /> {t('SMC_GEN_GO')}
                            </SMCLink>
                        </RowLayout>
                        {/* Remote management */}
                        <SMCTextInput label="" readOnly value="Remote management" />
                    </>
                )
            }
            {/* Mac address */}
            <SMCTextInput
                value={macAddress}
                onChange={e => setPageValue(pageSchema.macAddress, e.target.value)}
                label={t("SMP.GATEWAYS.PAGE.GENERAL.MAC_ADDRESS")}
                tooltip={t("SMP.GATEWAYS.PAGE.GENERAL.MAC_ADDRESS.TOOLTIP")}
                disabled={isClusterGateway || !modifyPermission}
                ignoreDisabledContext
            />
            {
                managedBySmp && (
                    /* Last connected IP */
                    <SMCTextInput
                        readOnly
                        value={lastConnectedIp}
                        label={t("SMP.GATEWAYS.PAGE.GENERAL.LAST_CON_IP_ADDRESS")}
                    />
                )
            }
            {/* Only show reg key and activation key when gateway is not cluster gateway and is managed by SMP */}
            {
                !isClusterGateway && managedBySmp && (
                    <>
                        {/* Registration key */}
                        <RowLayout>
                            <SMCTextInput
                                label={t("SMP.GATEWAYS.PAGE.GENERAL.REG_KEY")}
                                tooltip={t("SMP.GATEWAYS.PAGE.GENERAL.REG_KEY.TOOLTIP")}
                                value={registrationKey}
                                onChange={e => setPageValue(pageSchema.registrationKey, e.target.value)}
                                inline
                                ignoreDisabledContext
                                disabled={!modifyPermission}
                            />
                            <SMCButton onClick={() => { setPageValue(pageSchema.registrationKey, generateRandomString(8)) }}>
                                <img src={generateIcon} alt="Generate" />
                            </SMCButton>
                        </RowLayout>
                        {/* Activation key */}
                        <RowLayout>
                            <SMCTextInput
                                width={400}
                                disabled
                                value={activationKey}
                                label={t("SMP.GATEWAYS.PAGE.GENERAL.ACTIVATION_KEY")}
                            />
                            <SMCButton tooltip="Copy" onClick={copyActivationKey} style={{ height: 28, margin: 0 }}>
                                <Icon name={'copy'} width={15} height={15} />
                            </SMCButton>
                        </RowLayout>
                    </>
                )
            }
            {/* Show only if not managed by SMP */}
            {
                !managedBySmp && (
                    <>
                        {/* Static IP */}
                        <SMCTextInput
                            value={staticIp}
                            onChange={e => setPageValue(pageSchema.staticIp, e.target.value)}
                            label={t("SMC_GATEWAY_IP")}
                        />
                    </>
                )
            }
            {/* Floating windows */}
            <SetPlanModal
                isOpen={isChangePlanOpen}
                onRequestClose={closeChangePlan}
                hideDisabled={true}
                onSuccess={plan => {
                    setGatewayData(GatewayGetSchema.planId, plan.id);
                    setPlanObject(plan);
                    closeChangePlan();
                }}
                onError={e => {
                    enqueueErrorSnackbar(t(e));
                    closeChangePlan();
                }}
            />
        </EditorLayout>
    )
}

const mapStateToProps = null;

const mapDispatchToProps = {
    navigateTo: push
}

export default connect(mapStateToProps, mapDispatchToProps)(GatewayGeneral);